import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { TickerCard } from "./TickerCard.jsx";
import { Badge } from "./Primitives.jsx";
import { T } from "../lib/design.js";
import { SECTORS, ALL_TICKERS, SP500 } from "../lib/universe.js";
import { loadResults, saveResults } from "../lib/storage.js";
import { prescreen, analyze, parallelMap } from "../lib/api.js";
import { scoreFromData, toSignal } from "../lib/scoring.js";
import { CustomTickerInput } from "./CustomTickerInput.jsx";

// Top-N candidates to deeply analyze (the most expensive tier).
// Lowered from 12 to 5-8 → 60% reduction in expensive AI calls.
const TOP_N_ANALYZE = 8;

// Parallelism for analyze() — safe to run multiple in parallel since
// each is a separate API call. Cap at 3 to be polite to the API.
const ANALYZE_CONCURRENCY = 3;

export function Scanner({ resetSignal }) {
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState("IDLE"); // IDLE | PRESCREEN | ANALYZE | DONE | ERROR
  const [progress, setProgress] = useState({ done: 0, total: 0, label: "" });
  const [runCount, setRunCount] = useState(0);
  const [lastScanAt, setLastScanAt] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [sector, setSector] = useState("ALL");
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem("sp500_scanner_mode_v1") || "sp500";
    } catch { return "sp500"; }
  });
  const [customTickers, setCustomTickers] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("sp500_scanner_mode_v1", mode); } catch { /* ignore */ }
  }, [mode]);

  const abortRef = useRef(null);
  const runCountRef = useRef(0); // tracks latest run number to avoid stale state

  // Hydrate from localStorage on mount and when resetSignal changes
  useEffect(() => {
    const cached = loadResults();
    if (cached?.results?.length > 0) {
      setResults(cached.results);
      setRunCount(cached.runCount || 0);
      runCountRef.current = cached.runCount || 0;
      setLastScanAt(cached.scannedAt || null);
      setPhase("DONE");
      setSector(cached.sector || "ALL");
    }
    setHydrated(true);
  }, [resetSignal]);

  // Abort any in-flight scan on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // THE SCAN. Local accumulator pattern + computed runCount (avoids stale state).
  const runScan = useCallback(async () => {
    // Cancel any in-flight scan
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = controller.signal;

    // Compute next runCount synchronously — DO NOT rely on state.
    const nextRun = runCountRef.current + 1;
    runCountRef.current = nextRun;

    setError("");
    setResults([]);
    setPhase("PRESCREEN");
    setRunCount(nextRun);
    setProgress({ done: 0, total: 1, label: `Finding top momentum movers in ${sector === "ALL" ? "all sectors" : sector}…` });

    try {
      // ── TIER 0: Pre-screen — ONE call for top candidates ───────────────────
      const candidates = await prescreen({
        sector,
        count: 50,
        customTickers: mode === "custom" ? customTickers : undefined,
        signal,
      });

      if (signal.aborted) return;

      if (!candidates || candidates.length === 0) {
        setError("Pre-screen returned no candidates. Try again or use the Fix button.");
        setPhase("ERROR");
        return;
      }

      // Score and sort
      const scored = candidates.map((c) => {
        const score = scoreFromData(c);
        return { ...c, score, signal: toSignal(score) };
      }).sort((a, b) => b.score - a.score);

      // Show the prescreen results immediately
      setResults(scored);
      setProgress({ done: 1, total: 1, label: "Pre-screen complete" });

      if (signal.aborted) return;

      // ── TIER 1: Deep analyze top N — PARALLEL, one call per ticker ─────────
      const topN = scored
        .filter((r) => r.signal !== "NO_SIGNAL")
        .slice(0, TOP_N_ANALYZE);

      if (topN.length === 0) {
        setPhase("DONE");
        setLastScanAt(Date.now());
        saveResults({ results: scored, runCount: nextRun, scannedAt: Date.now(), sector });
        return;
      }

      setPhase("ANALYZE");
      setProgress({ done: 0, total: topN.length, label: "Analyzing top candidates in parallel" });

      // Mark them as analyzing for UI feedback
      const analyzingMap = new Map(topN.map((r) => [r.ticker, true]));
      setResults((curr) =>
        curr.map((r) => (analyzingMap.has(r.ticker) ? { ...r, analyzing: true } : r))
      );

      let completed = 0;
      const analyses = await parallelMap(
        topN,
        async (cand) => {
          if (signal.aborted) return null;
          try {
            const result = await analyze({
              ticker: cand.ticker,
              marketData: cand,
              signal,
            });
            return { ticker: cand.ticker, analysis: result };
          } catch (err) {
            if (err.name === "AbortError") return null;
            console.warn(`Analyze ${cand.ticker} failed:`, err.message);
            return { ticker: cand.ticker, error: err.message };
          } finally {
            if (!signal.aborted) {
              completed++;
              setProgress({ done: completed, total: topN.length, label: "Analyzing top candidates" });
              // Update partial results as each comes in
            }
          }
        },
        { concurrency: ANALYZE_CONCURRENCY, signal }
      );

      if (signal.aborted) return;

      // Merge analyses back into results
      const analysisMap = new Map();
      for (const a of analyses) {
        if (a?.ticker && a.analysis) analysisMap.set(a.ticker, a.analysis);
      }

      const finalResults = scored.map((r) => {
        const ai = analysisMap.get(r.ticker);
        const updated = { ...r, analyzing: false };
        if (ai) {
          updated.aiResult = ai;
          // Override score with AI's score if higher confidence
          if (typeof ai.score === "number") {
            updated.score = ai.score;
            updated.signal = ai.signal || updated.signal;
          }
        }
        return updated;
      }).sort((a, b) => b.score - a.score);

      const finishedAt = Date.now();
      setResults(finalResults);
      setLastScanAt(finishedAt);
      setPhase("DONE");

      // Persist — use nextRun, not runCount (which may be stale)
      saveResults({
        results: finalResults,
        runCount: nextRun,
        scannedAt: finishedAt,
        sector,
      });
    } catch (err) {
      if (err.name === "AbortError" || signal.aborted) return;
      console.error("Scan failed:", err);
      setError(err.message || "Unknown error");
      setPhase("ERROR");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [sector, mode, customTickers]);

  const cancelScan = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setPhase(results.length > 0 ? "DONE" : "IDLE");
    setProgress({ done: 0, total: 0, label: "" });
  }, [results.length]);

  // Derived
  const counts = useMemo(
    () => ({
      STRONG: results.filter((r) => r.signal === "STRONG").length,
      MODERATE: results.filter((r) => r.signal === "MODERATE").length,
      WATCH: results.filter((r) => r.signal === "WATCH").length,
    }),
    [results]
  );

  const filtered = useMemo(
    () =>
      results.filter((r) => {
        if (filter !== "ALL" && r.signal !== filter) return false;
        if (sector !== "ALL" && r.sector !== sector && !SP500[sector]?.includes(r.ticker)) return false;
        return true;
      }),
    [results, filter, sector]
  );

  const busy = phase === "PRESCREEN" || phase === "ANALYZE";
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const phaseLabel = {
    IDLE: "Ready",
    PRESCREEN: "Step 1/2 — Pre-screening market",
    ANALYZE: "Step 2/2 — Deep analysis on top candidates",
    DONE: "Complete",
    ERROR: "Error",
  }[phase];
  const phaseCol = {
    PRESCREEN: "#60a5fa",
    ANALYZE: "#f59e0b",
    DONE: "#22c55e",
    ERROR: "#f87171",
    IDLE: T.textMuted,
  }[phase];

  return (
    <div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: T.textPrimary,
              fontFamily: T.mono,
              letterSpacing: "-0.02em",
            }}
          >
            S&amp;P 500 Options Scanner
          </span>
          <Badge label="NYSE · S&P 500" col="#60a5fa" />
          <Badge label="2-tier AI pipeline" col="#22c55e" />
          {runCount > 0 && <Badge label={`Run #${runCount}`} col="#a855f7" />}
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6 }}>
          9 EMA retest · MACD (8/21/5) · SQZMOM higher low · {ALL_TICKERS.length} S&amp;P 500 tickers · 11 GICS
          sectors
          {lastScanAt && <span> · Last scan {new Date(lastScanAt).toLocaleString()}</span>}
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, alignItems: "flex-start" }}>
        {/* Mode toggle + mode-specific input */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: "1 1 260px", maxWidth: 480 }}>
          {/* Mode toggle */}
          <div style={{ display: "flex", gap: 4, padding: 3, background: T.bg1, borderRadius: 8, width: "fit-content" }}>
            {[
              ["sp500", "S&P 500"],
              ["custom", "Custom"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setMode(value)}
                disabled={busy}
                style={{
                  fontSize: 11,
                  fontWeight: mode === value ? 700 : 500,
                  padding: "5px 14px",
                  borderRadius: 6,
                  border: "none",
                  background: mode === value ? T.bg2 : "transparent",
                  color: mode === value ? T.textPrimary : T.textMuted,
                  boxShadow: mode === value ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Mode-specific input */}
          {mode === "sp500" ? (
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              disabled={busy}
              style={{
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 7,
                border: `0.5px solid ${T.borderStrong}`,
                background: T.bg2,
                color: T.textPrimary,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.5 : 1,
                maxWidth: 320,
              }}
            >
              <option value="ALL">All sectors ({ALL_TICKERS.length} tickers)</option>
              {SECTORS.map((s) => <option key={s} value={s}>{s} ({SP500[s].length})</option>)}
            </select>
          ) : (
            <CustomTickerInput
              disabled={busy}
              onTickersChange={setCustomTickers}
            />
          )}
        </div>

        <button
          onClick={runScan}
          disabled={!hydrated || (mode === "custom" && customTickers.length === 0)}
          style={{
            fontSize: 12,
            padding: "6px 18px",
            borderRadius: 7,
            fontWeight: 700,
            border: "0.5px solid #22c55e",
            background: "rgba(34,197,94,0.1)",
            cursor: hydrated ? "pointer" : "not-allowed",
            color: "#22c55e",
          }}
        >
          {busy ? `↻ Restart (${pct}%)` : results.length > 0 ? "↻ Re-run scan" : "▶ Run scan"}
        </button>

        {busy && (
          <button
            onClick={cancelScan}
            style={{
              fontSize: 12,
              padding: "6px 12px",
              borderRadius: 7,
              border: "0.5px solid rgba(248,113,113,0.5)",
              background: "rgba(248,113,113,0.08)",
              color: "#f87171",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            ✕ Cancel
          </button>
        )}

        {/* Filter pills */}
        <div style={{ display: "flex", gap: 5, marginLeft: "auto", flexWrap: "wrap" }}>
          {[
            ["ALL", "All"],
            ["STRONG", "Strong"],
            ["MODERATE", "Moderate"],
            ["WATCH", "Watch"],
          ].map(([v, l]) => {
            const cnt = v === "ALL" ? results.length : counts[v] ?? 0;
            const active = filter === v;
            const col = { ALL: "#60a5fa", STRONG: "#22c55e", MODERATE: "#f59e0b", WATCH: "#60a5fa" }[v];
            return (
              <button
                key={v}
                onClick={() => setFilter(v)}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 20,
                  cursor: "pointer",
                  border: `0.5px solid ${active ? col : T.border}`,
                  background: active ? col + "15" : "transparent",
                  color: active ? col : T.textMuted,
                  fontWeight: active ? 700 : 400,
                }}
              >
                {l}
                {cnt > 0 ? ` (${cnt})` : ""}
              </button>
            );
          })}
        </div>
      </div>

      {/* Progress */}
      {(busy || phase === "ERROR") && (
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11.5,
              color: T.textSecondary,
              marginBottom: 5,
              alignItems: "center",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {busy && (
                <span style={{ display: "inline-block", animation: "spin 1s linear infinite", color: phaseCol }}>
                  ⟳
                </span>
              )}
              <span style={{ color: phaseCol, fontWeight: 500 }}>{phaseLabel}</span>
              {progress.total > 0 && (
                <span style={{ color: T.textMuted }}>
                  · {progress.done}/{progress.total}
                </span>
              )}
            </span>
            {pct > 0 && <span style={{ fontFamily: T.mono, color: phaseCol }}>{pct}%</span>}
          </div>
          <div style={{ height: 3, borderRadius: 2, background: T.bg1, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                borderRadius: 2,
                background: phaseCol,
                transition: "width 0.25s",
              }}
            />
          </div>
          {progress.label && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 5 }}>{progress.label}</div>}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div
          style={{
            marginBottom: 14,
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(248,113,113,0.08)",
            border: "0.5px solid rgba(248,113,113,0.3)",
            color: "#f87171",
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 3 }}>⚠ Scan error</div>
          <div>{error}</div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#f87171cc" }}>
            Try clicking Run scan again. If the problem persists, use the Fix button at the bottom.
          </div>
        </div>
      )}

      {/* KPI strip */}
      {results.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 14 }}>
          {[
            ["Strong", counts.STRONG, "#22c55e"],
            ["Moderate", counts.MODERATE, "#f59e0b"],
            ["Watch", counts.WATCH, "#60a5fa"],
            ["Total", results.length, T.textMuted],
          ].map(([l, v, col]) => (
            <div key={l} style={{ background: T.bg1, borderRadius: 8, padding: "9px 12px" }}>
              <div
                style={{
                  fontSize: 10,
                  color: T.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 3,
                }}
              >
                {l}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: col, fontFamily: T.mono, lineHeight: 1 }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {results.length === 0 && !busy && (
        <div
          style={{
            textAlign: "center",
            padding: "52px 0",
            color: T.textMuted,
            borderRadius: 12,
            border: `0.5px dashed ${T.borderStrong}`,
          }}
        >
          <div style={{ fontSize: 26, marginBottom: 10 }}>◎</div>
          <div style={{ fontWeight: 700, marginBottom: 6, color: T.textSecondary, fontSize: 14 }}>
            {hydrated ? "Ready to scan" : "Loading…"}
          </div>
          <div style={{ fontSize: 12, maxWidth: 420, margin: "0 auto", lineHeight: 1.7, color: T.textMuted }}>
            {hydrated
              ? `Click "Run scan" to find today's top momentum candidates across the S&P 500. Optimized 2-step pipeline reduces API calls by ~85%. Results persist across page refresh.`
              : "Restoring previous results…"}
          </div>
        </div>
      )}

      {/* Results grid */}
      {filtered.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>
            {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: 12 }}>
            {filtered.map((r) => (
              <TickerCard key={r.ticker} r={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
